import { isChunkLoadError, recoverFromChunkLoadError } from '@shared/utils/appUpdate';
import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches chunk loading errors and other React errors.
 * When a chunk loading error is caught, it triggers a service worker update and page reload.
 */
export class ChunkErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chunk loading error caught by boundary:', error, errorInfo);

    if (isChunkLoadError(error) || errorInfo.componentStack?.includes('Suspense')) {
      console.log('Detected chunk loading error, triggering service worker update...');
      await recoverFromChunkLoadError();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#f5f5f5',
          }}
        >
          <div
            style={{
              maxWidth: '400px',
              padding: '30px',
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              textAlign: 'center',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
          >
            <h1 style={{ color: '#333', marginBottom: '20px' }}>Application Error</h1>
            <p style={{ color: '#666', lineHeight: '1.5', marginBottom: '20px' }}>
              We're updating the application to the latest version. Please wait...
            </p>
            <div
              style={{
                textAlign: 'center',
                marginTop: '20px',
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  width: '30px',
                  height: '30px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
